import React, { useState } from 'react';
import { TextField, IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Controller, useFormContext } from 'react-hook-form';
import { FormError } from './hook-form-error';
import useDebouncedValidation from '../../hooks/use-debounced-hookform-validation';
import { QueryKeyConfig } from '../../api/api-helper';
import { LoadingIndicator } from '../loading-indicator.component';

interface InputFieldProps {
  label?: string;
  placeHolder?: string;
  name: string;
  maxLength?: number;
  size?: number;
  multiline?: boolean;
  type?: string;
  autoFocus?: boolean;
  onChange?: any;
  disabled?: boolean;
  isShrink?: boolean;
  isValidateOnChange?: boolean;
  validationQueryRoute?: (value: any) => QueryKeyConfig;
  className?: string;
  required?: boolean;
}

export const FormInputPassword = ({
  name,
  label,
  size,
  multiline,
  type = 'text', // default to 'text' unless specified
  onChange,
  disabled,
  isShrink,
  isValidateOnChange,
  validationQueryRoute,
  className,
  required,
}: InputFieldProps): any => {
  const { control } = useFormContext();
  const { validate, isLoading } = useDebouncedValidation({ name, validationQueryRoute });

  const [showPassword, setShowPassword] = useState(false); // State to handle password visibility

  const handleChange = (val: any) => {
    onChange(val);
    if (!isValidateOnChange) {
      validate(val);
    }
  };

  const handleOnBlur = (val: any) => {
    validate(val);
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  return (
    <div className="form-group">
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, onBlur, value } }) => (
          <>
            <LoadingIndicator isLoading={isLoading} />
            <TextField
              name={name}
              label={label}
              variant="outlined"
              size="small"
              fullWidth
              multiline={multiline}
              rows={size ? size : 4}
              onBlur={(e) => {
                handleOnBlur(e.target.value);
              }}
              onChange={(e) => {
                onChange(e);
                handleChange(e.target.value);
              }}
              type={type === 'password' && !showPassword ? 'password' : 'text'} // Conditional type
              value={value}
              disabled={disabled}
              InputLabelProps={{
                shrink: !isShrink ? undefined : isShrink,
              }}
              InputProps={{
                inputProps: {
                  min: 0,
                },
                endAdornment:
                  type === 'password' ? (
                    <InputAdornment position="end">
                      <IconButton onClick={togglePasswordVisibility}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ) : null, // Show eye icon only for password fields
              }}
              className={`${className} ${required ? 'itsarequiredField' : ''} ${value ? 'has-data' : ''}`}
            />
          </>
        )}
      />
      <FormError name={name} />
    </div>
  );
};

FormInputPassword.defaultProps = {
  label: undefined,
  maxLength: undefined,
  placeHolder: undefined,
  onChange: () => {
    return;
  },
};

export default FormInputPassword;
